import { api } from './api.ts';
import { useAsync } from './useAsync.ts';
import { describeAge, formatDate, formatNumber, formatSigned, formatSignedPercent } from './format.ts';
import type {
  AuditRound,
  RoundMetricPoint,
  RoundMetricSeries,
  RoundSection,
  RoundsResponse,
  RoundUnit,
} from '../../../shared/types.ts';

/**
 * Rodadas de auditoria/conferência como série temporal (GET /api/rounds). As tabelas mostram
 * sempre três colunas (referência, anterior, atual) e deixam o resto para o histórico: o
 * tamanho da tela não cresce com o número de rodadas.
 */
export interface Rounds {
  loading: boolean;
  error: string | null;
  reload: () => void;
  /** Da mais antiga para a mais recente. */
  rounds: AuditRound[];
  /** A rodada mais recente de todas. */
  latest: AuditRound | null;
  /** Séries cuja chave começa com o prefixo (ex.: 'metricas.ig_insights_30d.'), na ordem em que chegaram. */
  seriesOf: (prefix: string) => RoundMetricSeries[];
  /** "Novo" só vale para o que a rodada mais recente de todas coletou. */
  isNew: (section: RoundSection) => boolean;
}

export function useRounds(): Rounds {
  const result = useAsync(() => api.get<RoundsResponse>('/rounds'), []);
  const rounds = result.data?.rounds ?? [];
  const series = result.data?.series ?? [];
  return {
    loading: result.loading,
    error: result.error,
    reload: result.reload,
    rounds,
    latest: rounds.at(-1) ?? null,
    seriesOf: (prefix) => series.filter((s) => s.metric.startsWith(prefix)),
    isNew: (section) => (rounds.at(-1)?.covers ?? []).includes(section),
  };
}

const decimals = (value: number): string => value.toFixed(1).replace('.', ',');

export function formatRoundValue(value: number | null, unit: RoundUnit): string {
  if (value === null) return '—';
  if (unit === 'pct') return `${decimals(value)}%`;
  if (unit === 'sec') return `${decimals(value)} s`;
  if (unit === 'brl') return `R$ ${value.toFixed(2).replace('.', ',')}`;
  if (unit === 'hours') return `${formatNumber(value)} h`;
  return formatNumber(value);
}

export interface RoundChange {
  text: string;
  tone: 'up' | 'down' | 'flat';
}

/** Variação entre dois valores: pontos percentuais para %, segundos para s, absoluto e % para o resto. */
export function describeRoundChange(before: number | null, after: number | null, unit: RoundUnit): RoundChange | null {
  if (before === null || after === null) return null;
  const diff = after - before;
  if (diff === 0) return { text: 'sem mudança', tone: 'flat' };
  const tone = diff > 0 ? 'up' : 'down';
  const sign = diff > 0 ? '+' : '−';
  if (unit === 'pct') return { text: `${sign}${decimals(Math.abs(diff))} p.p.`, tone };
  if (unit === 'sec') return { text: `${sign}${decimals(Math.abs(diff))} s`, tone };
  if (unit === 'brl') return { text: `${sign}R$ ${Math.abs(diff).toFixed(2).replace('.', ',')}`, tone };
  const percent = before !== 0 ? ` (${formatSignedPercent((diff / before) * 100)})` : '';
  return { text: `${formatSigned(diff)}${percent}`, tone };
}

/** "Janela 21/08/2026 a 20/09/2026 · Fonte: …": o que está por trás de um número, para dica e histórico. */
export function describePoint(p: RoundMetricPoint): string {
  const parts: string[] = [];
  if (p.windowStart && p.windowEnd) parts.push(`Janela: ${formatDate(p.windowStart)} a ${formatDate(p.windowEnd)}`);
  if (p.windowNote) parts.push(p.windowNote);
  if (p.observedOn !== p.roundDate) parts.push(`Número de ${formatDate(p.observedOn)}`);
  parts.push(`Fonte: ${p.source}`);
  if (p.note) parts.push(p.note);
  return parts.join(' · ');
}

/**
 * Por que as séries de uma tabela não têm dado na rodada mais recente, se não têm. Procura do mais
 * específico (métricas não coletadas dentro de uma seção coberta) ao mais geral (seção não coberta).
 */
export function missingInLatest(
  latest: AuditRound | null,
  section: RoundSection,
  series: RoundMetricSeries[],
  lastRoundWithData: string | null,
): { reason: string; latestDate: string; dataDate: string | null; age: string | null } | null {
  if (!latest || latest.roundDate === lastRoundWithData) return null;
  const first = series[0]?.metric ?? '';
  const specific = latest.notCollected.find((n) => first.startsWith(n.metricPrefix));
  const general = latest.notCovered.find((n) => n.section === section);
  const reason =
    specific?.reason ??
    general?.reason ??
    'A rodada não trouxe estas métricas (sem motivo registrado no arquivo da rodada).';
  return {
    reason,
    latestDate: latest.roundDate,
    dataDate: lastRoundWithData,
    age: lastRoundWithData ? describeAge(lastRoundWithData) : null,
  };
}
